import React from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 20, 25, 30];

function getPageNumbers(currentPage, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

function TablePagination({
  currentPage,
  totalItems,
  rowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onPageChange,
  onRowsPerPageChange,
  itemLabel = "items",
  rowsPerPageSelectId = "table-rows-per-page",
}) {
  if (totalItems <= 0) {
    return null;
  }

  const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || rowsPerPageOptions[0] || 5);
  const totalPages = Math.max(1, Math.ceil(totalItems / safeRowsPerPage));
  const startIndex = (currentPage - 1) * safeRowsPerPage;
  const startRow = startIndex + 1;
  const endRow = Math.min(startIndex + safeRowsPerPage, totalItems);
  const showPageNav = totalItems > safeRowsPerPage;

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const next = Number(e.target.value);
    if (rowsPerPageOptions.includes(next)) {
      onRowsPerPageChange(next);
    }
  };

  return (
    <div className="pagination pagination--with-rows-select">
      <div className="pagination-main">
        {showPageNav ? (
          <>
            <button
              type="button"
              className="pagination-btn pagination-arrow"
              onClick={handlePrevious}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <FaChevronLeft />
            </button>
            <div className="page-numbers">
              {getPageNumbers(currentPage, totalPages).map((page, index) =>
                page === "..." ? (
                  <span key={`ellipsis-${index}`} className="page-number ellipsis">
                    ...
                  </span>
                ) : (
                  <span
                    key={page}
                    role="button"
                    tabIndex={0}
                    className={`page-number ${currentPage === page ? "active" : ""}`}
                    onClick={() => onPageChange(page)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onPageChange(page);
                      }
                    }}
                  >
                    {page}
                  </span>
                )
              )}
            </div>
            <button
              type="button"
              className="pagination-btn pagination-arrow"
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <FaChevronRight />
            </button>
          </>
        ) : null}
        <div className="pagination-info">
          {startRow}-{endRow} of {totalItems} {itemLabel}
        </div>
      </div>

      <div className="pagination-rows-per-page">
        <label htmlFor={rowsPerPageSelectId}>Rows per page</label>
        <select
          id={rowsPerPageSelectId}
          className="pagination-rows-per-page__select"
          value={safeRowsPerPage}
          onChange={handleRowsPerPageChange}
          aria-label="Rows per page"
        >
          {rowsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default TablePagination;
